import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFormContext } from 'react-hook-form';

interface Strength {
  id: string;
  description: string;
}

export const useStrengths = () => {
  const [strengths, setStrengths] = useState<Strength[]>([]);
  const { setValue, getValues } = useFormContext();
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize strengths from form values only once
  useEffect(() => {
    const formStrengths = getValues('strengths');
    if (Array.isArray(formStrengths) && formStrengths.length > 0) {
      setStrengths(
        formStrengths.map((strength: any) => ({
          id: uuidv4(),
          description: strength.description
        }))
      );
    }
  }, []); // Empty dependency array to run only once

  // Memoize the update function to prevent unnecessary re-renders
  const updateFormValues = useCallback((newStrengths: Strength[]) => {
    if (isUpdating) return; // Prevent duplicate updates
    
    setIsUpdating(true);
    
    // Format strengths for the form
    const formattedStrengths = newStrengths.map((strength, index) => ({
      description: strength.description,
      order_index: index
    }));
    
    // Use setTimeout to defer the update and prevent focus loss
    setTimeout(() => {
      setValue('strengths', formattedStrengths, { 
        shouldDirty: true 
      });
      setIsUpdating(false);
    }, 0);
  }, [setValue, isUpdating]);

  // Batch state updates with form updates to minimize re-renders
  const handleAddStrength = useCallback(() => {
    const newStrength = { id: uuidv4(), description: '' };
    const newStrengths = [...strengths, newStrength];
    setStrengths(newStrengths);
    updateFormValues(newStrengths);
  }, [strengths, updateFormValues]);

  const handleRemoveStrength = useCallback((index: number) => {
    const newStrengths = strengths.filter((_, i) => i !== index);
    setStrengths(newStrengths);
    updateFormValues(newStrengths);
  }, [strengths, updateFormValues]);

  const handleStrengthChange = useCallback((index: number, value: string) => {
    // Update local state immediately to keep the UI responsive
    const newStrengths = [...strengths];
    newStrengths[index].description = value;
    setStrengths(newStrengths);
    
    // Debounce form updates to prevent focus loss during typing
    const debounceTimer = setTimeout(() => {
      updateFormValues(newStrengths);
    }, 500);
    
    // Clean up timer on next update
    return () => clearTimeout(debounceTimer);
  }, [strengths, updateFormValues]);

  const handleMoveStrength = useCallback((index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === strengths.length - 1)
    ) {
      return;
    }

    const newStrengths = [...strengths];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newStrengths[index], newStrengths[newIndex]] = [newStrengths[newIndex], newStrengths[index]];
    
    setStrengths(newStrengths);
    updateFormValues(newStrengths);
  }, [strengths, updateFormValues]);

  return {
    strengths,
    handleAddStrength,
    handleRemoveStrength,
    handleStrengthChange,
    handleMoveStrength
  };
};